import { selectMarketMapKeys } from "@/features/survey/surveySelectors";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldModal } from "@emerald-react/modal";
import { EmeraldButtonSize, EmeraldButtonVariant } from "@emerald/nxcore";
import { useAddSurveyMapKeysMutation } from "@services/survey-mapKeys";
import { useCreatePOICategoryMutation } from "@services/survey-poi";
import type {
  SurveyPOICategory,
  SurveyPOICategoryRequest,
} from "@survey-types/survey.poi.types";
import { BaseSyntheticEvent, useState } from "react";
import { useSelector } from "react-redux";

interface CategoryCreationModalProps {
  open: boolean;
  onClose: () => void;
  onCategoryCreated: (category: SurveyPOICategory) => void;
  surveyId: string;
  existingCategories: SurveyPOICategory[];
}

const CategoryCreationModal = ({
  open,
  onClose,
  onCategoryCreated,
  surveyId,
  existingCategories,
}: CategoryCreationModalProps) => {
  const [createCategory, { isLoading }] = useCreatePOICategoryMutation();
  const [addSurveyMapKeys] = useAddSurveyMapKeysMutation();
  const currentMapKeys = useSelector(selectMarketMapKeys);

  const [categoryName, setCategoryName] = useState<string>("");
  const [categoryNameError, setCategoryNameError] = useState<string>("");

  const handleCategoryNameChange = (value: string) => {
    setCategoryName(value);
    setCategoryNameError("");
  };

  const validateForm = (): boolean => {
    const trimmedName = categoryName.trim();

    if (!trimmedName) {
      setCategoryNameError("Category name is required");
      return false;
    }

    if (trimmedName.length < 2) {
      setCategoryNameError("Category name must be at least 2 characters");
      return false;
    }

    if (trimmedName.length > 50) {
      setCategoryNameError("Category name must be less than 50 characters");
      return false;
    }

    // Check for duplicate (case-sensitive — different capitalisation is allowed)
    const isDuplicate = existingCategories.some(
      (cat) => cat.categoryName === trimmedName,
    );

    if (isDuplicate) {
      setCategoryNameError(
        "This Category name is already in use.",
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload: SurveyPOICategoryRequest = {
        categoryName: categoryName.trim(),
        pinType: "ICON",
        iconData: {
          color: "#a23f97", // Default purple (matches PoiColorPicker default)
          iconName: "circle", // Default circle icon
        },
        pointSize: 28, // Default size
      };

      const newCategory = await createCategory({
        surveyId,
        body: payload,
      }).unwrap();

      // Create the corresponding mapkey for this POI category.
      // Mapkeys for POIs are only created here (on category creation) and
      // updated via rename — never through the global MarketMapKey sync.
      const color = newCategory.iconData?.color || "#a23f97";
      addSurveyMapKeys({
        surveyId,
        mapKeys: [
          {
            label: newCategory.categoryName,
            color,
            active: true,
            isCurrentLocation: false,
            order: currentMapKeys.length,
            category: "POI",
            referenceId: newCategory.extId,
          },
        ],
      });

      // Reset form
      setCategoryName("");
      setCategoryNameError("");

      // Notify parent
      onCategoryCreated(newCategory);
    } catch (error) {
      console.error("Failed to create category:", error);
      setCategoryNameError("Failed to create category. Please try again.");
    }
  };

  const handleCancel = () => {
    setCategoryName("");
    setCategoryNameError("");
    onClose();
  };

  const trimmedName = categoryName.trim();
  const isTooShort = trimmedName.length > 0 && trimmedName.length < 2;
  const isTooLong = trimmedName.length > 50;
  const isDuplicate = existingCategories.some(
    (cat) => cat.categoryName === trimmedName,
  );

  const getDisplayError = (): string => {
    if (isTooShort) return "Category name must be at least 2 characters";
    if (isTooLong) return "Category name must be less than 50 characters";
    if (isDuplicate)
      return "This Category name is already in use.";
    return categoryNameError;
  };

  const displayError = getDisplayError();

  const isFormValid =
    trimmedName.length >= 2 &&
    !isTooLong &&
    !isDuplicate &&
    !categoryNameError;

  return (
    <EmeraldModal
      opened={open}
      onClose={handleCancel}
      headerText="Create New Category"
      hasCloseIcon
      zIndex={1000}
    >
      <div className="grid grid-flow-row gap-4 p-2">
        <EmeraldInput
          label="Category Name"
          placeholder="Enter category name"
          value={categoryName}
          onChange={(e: BaseSyntheticEvent) =>
            handleCategoryNameChange(e.target.value)
          }
          maxLength={50}
        />

        {/* Error / validation message */}
        {displayError && (
          <p className="mt-1 text-sm text-red-600">{displayError}</p>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <EmeraldButton
            label="Cancel"
            variant={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            onClick={handleCancel}
            disabled={isLoading}
            nativeProps={{ type: "button" }}
          />
          <EmeraldButton
            label="Create Category"
            variant={EmeraldButtonVariant.Primary}
            size={EmeraldButtonSize.Medium}
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
            nativeProps={{ type: "button" }}
          />
        </div>
      </div>
    </EmeraldModal>
  );
};

export default CategoryCreationModal;
